// Транспорт печатных форм из СВОЕЙ базы: счёт, договор-счёт, карточка договора, КП, акт.
//
// ЧЕМ ЭТО ОТЛИЧАЕТСЯ ОТ lib/print1c.ts. Там бланки собираются В БРАУЗЕРЕ из данных 1С,
// и это верно для документа, который читают глазами. Здесь лист приходит ГОТОВОЙ
// СТРОКОЙ из ядра. Причина одна и она не про удобство: решение «печатать нельзя»
// принимает ядро, рядом с данными. Собирай лист браузер — правила отказа пришлось бы
// повторить в TypeScript, и однажды браузер напечатал бы то, от чего ядро отказалось.
//
// ПОЭТОМУ ЗДЕСЬ НЕТ НИ ОДНОЙ ПРОВЕРКИ ДАННЫХ. Экран не решает, годится ли ИНН и сходится
// ли сумма: он показывает то, что ответило ядро. Появись такая проверка тут — она стала
// бы вторым мнением, и рано или поздно разошлась бы с первым.
//
// Записи нет и быть не может: печать ничего никуда не сохраняет.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/print'

async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) throw new Error('раздел «Печатные формы» вам не открыт')
    // 400 — неверный параметр (не число, ноль). Пустой лист здесь читался бы как
    // «документа нет», а это разные вещи: «спросили не то» и «нечего печатать».
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

/** Отказ или оговорка. Код — для экрана, текст — для человека. */
export interface Замечание {
  code: string
  text: string
}

/**
 * Ответ печатной формы.
 *
 * `ok: false` — это НЕ ошибка запроса, а ответ по существу: «печатать нельзя, вот
 * почему». Поэтому он приходит кодом 200 и обрабатывается тут же, а не в catch.
 * `html` при этом равен `null` всегда: ядро не собирает лист, от которого отказалось.
 */
export interface ОтветФормы {
  form: string
  ok: boolean
  html: string | null
  refusals: Замечание[]
  warnings: Замечание[]
  hint?: string
  data: Record<string, unknown>
}

export interface ОписаниеФормы {
  form: string
  path: string
  param: string
  title: string
  needs: string
  source: string
}

export interface НашеЮрлицо {
  id: number
  name: string | null
  inn: string | null
  kpp: string | null
  bank_ok: boolean
  bank_name: string | null
  banks: number
  addresses: number
}

export interface СправкаПечати {
  source: string
  forms: ОписаниеФормы[]
  counts: Record<string, number>
  own_companies: НашеЮрлицо[]
  data_notes: Record<string, string>
  refusal_codes: string[]
}

/** Что сегодня вообще печатается: счётчики таблиц и готовность наших юрлиц. */
export function useСправкаПечати() {
  return useQuery({
    queryKey: ['print', 'forms'],
    queryFn: () => запрос<СправкаПечати>('/forms'),
    staleTime: 60_000,
  })
}

/**
 * Собрать лист. Отдельной функцией, а не хуком: печать — действие по нажатию, и
 * подтягивать её кэшем react-query значило бы однажды напечатать вчерашний лист.
 */
export function собратьЛист(форма: ОписаниеФормы, ид: number): Promise<ОтветФормы> {
  const путь = форма.path.replace('/print', '')
  return запрос<ОтветФормы>(`${путь}?${форма.param}=${encodeURIComponent(String(ид))}`)
}
